/* eslint-disable no-multi-spaces */
/**
 * User class
 */
import debug from 'debug'
import xml2js from 'xml2js'
import Call from './call'

const SET_PRESENCE_NOTE = Symbol('Set presence note text member')
const SET_PRESENCE_STATUS = Symbol('Set user status text')
const PARSE_PRESENCE = Symbol('Parse pidf/xpidf XML presence doc')
const INIT_STATUS = Symbol('Init user status')

const OFFLINE   = Symbol('User satus: OFFLINE')
const ONLINE    = Symbol('User satus: ONLINE')
const RINGING   = Symbol('User satus: RINGING')
const BUSY      = Symbol('User satus: BUSY')
const AWAY      = Symbol('User satus: AWAY')
const ONCALL    = Symbol('User satus: ONCALL')

const dbg = debug('store:class:user:')

export default class {
  constructor (data) {
    let pictureProfileUrl = null
    if (data.picture_profile_url) {
      pictureProfileUrl = new URL(data.picture_profile_url)
      pictureProfileUrl.searchParams.set('size', '64x64')
    }

    this.id                = data.subcriber_id // subscriber ID
    this.userId            = data.user_id // user ID
    this.exten             = data.exten
    this.chatUsername      = data.chat_username
    this.name              = data.name
    this.pictureProfileUrl = pictureProfileUrl?.href
    this.contact           = data.contact
    this.user_agent        = data.user_agent
    this.vmnum             = data.vmnum
    this.calls             = []
    this.status            = OFFLINE
    this.isDND             = !!data.dnd
    this.hasFwd            = !!data.forward
    this.hasFme            = !!data.followme

    data.calls.forEach(c => {
      const call = new Call(c, c.direction === 'IN')
      call.setConfirmed()
      this.calls.push(call)
    })

    this.department = data.department
    this.registered = data.contact?.length > 0
    this[INIT_STATUS]()
  }

  /**
   * Find user by extension.
   *
   * @param users Array
   * @param exten Extension to look for
   * @return User when found or null
   */
  static findByExten (users, exten) {
    return users.find((user) => user.exten === exten) || null
  }

  /**
   * Check if user already exists in users array (store)
   *
   * @param storeUsers Array of users
   * @param user User to check. User class object
   * @return boolean True if in list
   */
  static exists (storeUsers, user) {
    return undefined !==
      storeUsers.find((u) => u.exten === user.exten)
  }

  /**
   * Set user offline when SIP status changed.
   * @param {Array} Users array in store
   * @param {String} User to update
   */
  static setUserOffline (users, exten) {
    const user = this.findByExten(users, exten)
    if (!user) {
      return
    }
    user.setOffline()
    user.note = user.statusTextKey
  }

  /**
   * Set user online when SIP register received.
   * Check if user is already online and if user is
   * online, do not change anything as it is just a
   * session update. Change state only if user status
   * is offline.
   * @param {Array} Users array in store
   * @param {String} User to update
   */
  static setUserOnline (users, exten) {
    const user = this.findByExten(users, exten)
    if (!user || !user.isOffline) {
      return
    }
    user.setOnline()
    user.note = user.statusTextKey
  }

  /**
   * proxy for Call class static method
   */
  static fromuserName (fromuser) {
    return Call.fromuserName(fromuser)
  }

  static fromuserExten (fromuser) {
    return Call.fromuserExten(fromuser)
  }

  /* Getters */
  get isOffline () { return this.status === OFFLINE }
  get isOnline  () { return this.status === ONLINE }
  get isRinging () { return this.status === RINGING }
  get isBusy    () { return this.status === BUSY }
  get isAway    () { return this.status === AWAY }
  get isOnCall  () { return this.calls?.some(call => call.isConfirmed) === true } // status can be reset because of REGISTERs. Still check if CONFIRMED calls are present.
  get statusTextKey () {
    let i18nkey
    switch (this.status) {
      case ONLINE:
        i18nkey = 'buddy.status.online'
        break
      case OFFLINE:
        i18nkey = 'buddy.status.offline'
        break
      case RINGING:
        i18nkey = 'buddy.status.calling'
        break
      case BUSY:
        i18nkey = 'buddy.status.busy'
        break
      case AWAY:
        i18nkey = 'buddy.status.away'
        break
      case ONCALL:
        i18nkey = 'buddy.status.oncall'
        break
      default:
        i18nkey = 'buddy.status.unknown'
        break
    }
    dbg(`Set statusTextKey ${this.exten}: [${this.Status}] "${i18nkey}"`)
    return i18nkey
  }

  get statusColor () {
    let color
    switch (this.status) {
      case ONLINE:
        color = 'green'
        break
      case OFFLINE:
        color = 'blue-grey lighten-1'
        break
      case RINGING:
        color = 'blue'
        break
      case BUSY:
        color = 'red accent-4'
        break
      case AWAY:
        color = 'orange'
        break
      case ONCALL:
        color = 'red accent-4'
        break
      default:
        color = 'grey lighten-1'
        break
    }
    dbg(`Set statusColor ${this.exten}: [${this.Status}] "${color}"`)
    return color
  }

  get Status () {
    switch (this.status) {
      case OFFLINE:
        return 'OFFLINE'
      case ONLINE:
        return 'ONLINE'
      case RINGING:
        return 'RINGING'
      case BUSY:
        return 'BUSY'
      case AWAY:
        return 'AWAY'
      case ONCALL:
        return 'ONCALL'
      default:
        return 'UNKNOWN'
    }
  }

  /* @public */

  setOffline () { this.status = OFFLINE }
  setOnline  () { this.status = ONLINE }
  setRinging () { this.status = RINGING }
  setBusy    () { this.status = BUSY }
  setAway    () { this.status = AWAY }
  setOnCall  () { this.status = ONCALL }
  setBuddy   (sort) {
    this.buddy = true
    this.buddy_sort = sort
  }

  /**
   * Init user call
   *
   * @param call Call data
   * @return void
   */
  callInit (data) {
    this.calls.push(Call.initiate(data, (data.touser === this.exten)))
    dbg(`User ${this.exten} (${this.Status}) new call start.`)
    if (!this.isOnCall) {
      // save current state
      this.presence = this.status
      this.presenceNote = this.note
      // set new state
      this.setRinging()
      this.note = this.statusTextKey
    }
  }

  /**
   * Add call form AttendedTransfer Event as transfer user
   *
   * @param xfd Object AttendedTransfer AMI event data
   * @return void
   */
  addAttXferUserCall (xfd) {
    const data = {
      user: xfd.transfereecalleridnum,
      name: xfd.transfereecalleridname,
      callid: xfd.transfereechanvariable?.SIPCALLID,
      legClid: xfd.transfertargetchanvariable?.SIPCALLID,
      state: null,
      astchan: xfd.transfereechanvariable?.BRIDGEPEER,
      amisrvid: xfd.amisrvid,
      amihost: xfd.amihost
    }
    data.name = data.name === '<unknown>' ? '' : data.name
    const call = new Call(data, true)
    call.setConfirmed()
    this.calls.push(call)
    this.setOnCall()
  }

  /**
   * Add call form AttendedTransfer Event as transfer target
   *
   * @param xfd Object AttendedTransfer AMI event data
   * @return void
   */
  addAttXferTargetCall (xfd) {
    const data = {
      user: xfd.transfertargetcalleridnum,
      name: xfd.transfertargetcalleridname,
      callid: xfd.transfertargetchanvariable?.SIPCALLID,
      legClid: xfd.transfereechanvariable?.SIPCALLID,
      state: null,
      astchan: xfd.transfertargetchanvariable?.BRIDGEPEER,
      amisrvid: xfd.amisrvid,
      amihost: xfd.amihost
    }
    data.name = data.name === '<unknown>' ? '' : data.name
    const call = new Call(data, false)
    call.setConfirmed()
    this.calls.push(call)
    this.setOnCall()
  }

  /**
   * Set user call is parked.
   *
   * @param data Object Call data from AMI event ParkedCall
   * @return void
   */
  setParked (data) {
    const call = Call.initParked(data)
    this.calls.push(call)
    this.setOnCall()
  }

  /**
   * Change user status to on call (confirmed)
   *
   * @param call Call data
   * @return void
   */
  callConfirmed (data) {
    this.calls.forEach((call) => {
      if (call.callid === data['call-id']) {
        call.setConfirmed()
        call.setStartTime(data.utime)
        call.setNames(data)
      }
    })
    this.calls.sort((a, b) => a.startTime > b.startTime)
    this.setOnCall()
    this.note = this.statusTextKey
  }

  /**
   * Update call with Asterisk channel identifier and Asterisk pbx id and host
   *
   * @param data Asterisk channel data from AMI
   * @param callid
   * @return void
   */
  updateCallChannel (data, callid) {
    this.calls.forEach((call) => {
      if (call.callid === callid) {
        call.astchan = data.channel
        call.amisrvid = data.amisrvid
        call.asthost = data.amihost
      }
    })
  }

  /**
   * Change user status when call hangup (terminated)
   *
   * @param {Number} Call index in calls array
   * @return void
   */
  callTerminated (idx) {
    this.calls.splice(idx, 1)
    dbg(`User ${this.exten} call terminated`)
    if (this.calls.length === 0) {
      this.status = this.presence === RINGING ? ONLINE : this.presence
      this.note = this.presenceNote
    } else {
      this.calls.sort((a, b) => a.startTime > b.startTime)
    }
  }

  /**
   * When parked call retrieved set users calls.
   * This will set up parkee call.
   *
   * @param data Object AMI event UnParkedCall
   * @return void
   */
  setUnparkedParkeeCall (data) {
    const callData = {
      user: data.parkeecalleridnum,
      name: data.parkeecalleridname,
      callid: data.parkeechanvariable?.SIPCALLID,
      legClid: data.retrieverchanvariable?.SIPCALLID,
      astchan: data.parkeechannel,
      amisrvid: data.amisrvid,
      amihost: data.amihost
    }
    const call = new Call(callData, false)
    call.setConfirmed()
    this.calls.push(call)
    this.setOnCall()
  }

  /**
   * When parked call retrieved set users calls.
   * This will set up retriever call.
   *
   * @param data Object AMI event UnParkedCall
   * @return void
   */
  setUnparkedRetrieveCall (data) {
    const callData = {
      user: data.retrievercalleridnum,
      name: data.retrievercalleridname,
      callid: data.retrieverchanvariable?.SIPCALLID,
      legClid: data.parkeechanvariable?.SIPCALLID,
      astchan: data.retrieverchannel,
      amisrvid: data.amisrvid,
      amihost: data.amihost
    }
    const call = new Call(callData, true)
    call.setConfirmed()
    this.calls.push(call)
    this.setOnCall()
  }

  /**
   * Check if call with callid is already present.
   * Helps to avoid duplicated calls
   *
   * @param callid String call with call-id already exists
   * @return Boolean
   */
  hasCall (callid) {
    return this.calls.filter(call => call.callid === callid).length > 0
  }

  /**
   * Set presence from xml body
   *
   * @param data Message from RabbitMQ
   * @return void
   */
  setPresence (data) {
    // do not change user on call status
    if (this.isOnCall) return
    if (data.body === undefined || data.body.length === 0) return

    xml2js.parseString(data.body, function (err, result) {
      let presence = null
      if (err) {
        dbg(`Failed to parse doc: ${data.body}`)
      } else {
        presence = this[PARSE_PRESENCE](result)
        if (presence) {
          this[SET_PRESENCE_STATUS](presence.status)
          this[SET_PRESENCE_NOTE](presence.note)
        }
      }
    }.bind(this))
  }

  setPrefs (data) {
    switch (data.pref) {
      case 'DND':
        this.isDND = data.enabled
        break
      case 'FORWARD':
        this.hasFwd = data.enabled
        break
      case 'FOLLOWME':
        this.hasFme = data.enabled
        break
    }
  }

  updateVmail (data) {
    const vmsg = data.body?.split(';')
      .map(e => e.split(':').map(l => l?.toLowerCase().trim()))
      .find(e => e[0] === 'voice-message')

    if (!vmsg) return // not found
    const num = vmsg[1].split('/')

    if (num.length < 2) return // wrong format
    this.vmnum = ~~num[0]
  }

  /**
   * @private methods
   */
  [ INIT_STATUS ] () {
    if (Call.confirmedList(this.calls).length > 0) {
      this.setOnCall()
      this.presence = this.registered ? ONLINE : OFFLINE
    } else {
      this.status = this.registered ? ONLINE : OFFLINE
    }
    this.note = this.statusTextKey
    this.presenceNote = this.note
    if (this.isDND) this.setBusy()
  }

  [ PARSE_PRESENCE ] (body) {
    let note = null
    if (!body || !body.presence || !Array.isArray(body.presence.tuple)) {
      return false
    }
    const tuple = body.presence.tuple[0]

    if (!tuple || !Array.isArray(tuple.status) || !Array.isArray(tuple.status[0].basic)) {
      return false
    }
    const status = tuple.status[0].basic[0]

    if (Array.isArray(tuple.note)) {
      note = tuple.note[0]
    }

    // polycom dm:person
    if (Array.isArray(body.presence['dm:person'])) {
      const person = body.presence['dm:person'][0]
      if (Array.isArray(person['rp:activities'])) {
        const act = person['rp:activities'][0]
        if (act['rp:other']) {
          note = act['rp:other'][0]
        } else {
          ['busy', 'other', 'away', 'lunch', 'online'].forEach((tag) => {
            // if (act.hasOwnProperty(`rp:${tag}`)) {
            if (Object.prototype.hasOwnProperty.call(act, `rp:${tag}`)) {
              note = tag
            }
          })
        }
      }
    }

    return { status: status, note: note }
  }

  [ SET_PRESENCE_STATUS ] (status) {
    switch (status) {
      case 'closed':
        this.setBusy()
        this.presence = BUSY
        this.isDND = true
        break
      case 'open':
        this.setOnline()
        this.presence = ONLINE
        this.isDND = false
        break
      default:
        break
    }
  }

  [ SET_PRESENCE_NOTE ] (note) {
    this.note = note
    switch (note?.toLowerCase()) {
      case 'online':
        this.note = this.statusTextKey
        break
      case 'busy':
        this.note = this.statusTextKey
        break
      case 'away':
        this.setAway()
        this.presence = AWAY
        this.note = this.statusTextKey
        break
      case 'lunch':
        this.note = 'buddy.status.onlunch'
        break
      case 'offline':
        this.note = 'buddy.status.offline'
        break
      default:
        break
    }
  }
}
